import React, { useState, useEffect } from 'react';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import RTPageLayout from '../common/RTPageLayout';

const AddComplaintType = ({ type, onClose }) => {
  const location = useLocation();
  const isEditMode = type && type.type_id; // More explicit edit mode detection

  const [form, setForm] = useState({
    type_name: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode) {
      setForm({
        type_name: type.type_name || '',
        description: type.description || '',
      });
    } else {
      setForm({
        type_name: '',
        description: '',
      });
    }
  }, [type, isEditMode]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.type_name.trim()) {
      newErrors.type_name = 'Type name is required';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        // Edit existing type
        await apiClient.post(`/complaints/types/${type.type_id}/update`, form);
        toastSuccess('Complaint type updated successfully!');
      } else {
        // Add new type
        await apiClient.post('/complaints/types', form);
        toastSuccess('Complaint type added successfully!');
      }
      onClose();
    } catch (error) {
      console.error('Error saving complaint type:', error);

      // Show generic error messages
      let errorMessage = 'Failed to save complaint type';

      // Check for duplicate errors more comprehensively
      const errorDetail = error.response?.data?.detail || '';
      const errorMessage_response = error.response?.data?.message || '';
      const allErrorText = (
        errorDetail +
        ' ' +
        errorMessage_response
      ).toLowerCase();

      if (
        error.response?.status === 409 ||
        allErrorText.includes('duplicate') ||
        allErrorText.includes('already exists') ||
        allErrorText.includes('unique constraint') ||
        allErrorText.includes('integrity constraint')
      ) {
        errorMessage = 'A complaint type with this name already exists';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided';
      } else if (error.response?.status === 404) {
        errorMessage = 'Complaint type not found';
      }

      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RTPageLayout
      activePath={location.pathname}
      title={<>{isEditMode ? 'Edit Complaint Type' : 'Add Complaint Type'}</>}
      actions={
        <>
          <RTButton variant="secondary" onClick={onClose}>
            ← Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <form onSubmit={handleSubmit} className="complaint-form">
        <div className="complaint-form-sections">
          <div className="complaint-section">
            <h3 className="complaint-section-title">Type Details</h3>

            <div className="complaint-form-group">
              <label>
                Type Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="type_name"
                value={form.type_name}
                onChange={handleChange}
                placeholder="Enter complaint type name"
                required
              />
              {errors.type_name && (
                <span className="form-error">{errors.type_name}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Enter description (optional)"
                rows="4"
              />
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <RTSpinner
              size="medium"
              showMessage={true}
              message="Saving complaint type..."
            />
          </div>
        )}

        <div className="complaint-actions">
          <RTButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </RTButton>
          <RTButton type="submit" variant="primary" disabled={loading}>
            {loading
              ? 'Saving...'
              : isEditMode
                ? 'Update Complaint Type'
                : 'Add Complaint Type'}
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddComplaintType;
